import { FormControl, MenuItem, MenuProps, Select } from '@mui/material';
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import { Text } from 'components/Typography';
import { addMinutesDate, diffMinutes, endOfDayDate, formatTime12, fullHoursToText } from 'utils/time';
import { customErrorBorderStyles, makeCustomSelectStyles } from '../../customStyles';
import styles from './ExpirySelectBlock.scss';

type ExpirySelectProps = {
  selectedTime: Date;
  expiredTime?: Nullable<Date>;
  onExpiredTimeSelected: Dispatch<SetStateAction<Nullable<Date>>>;
  error?: string;
  disabled?: boolean;
};

type SelectValue = number | 'eod';

export const ExpirySelectBlock: React.FC<ExpirySelectProps> = ({ selectedTime, expiredTime, onExpiredTimeSelected, error, disabled = false }) => {
  const customSelectStyles = makeCustomSelectStyles();
  const menuProps: Partial<MenuProps> = {
    autoFocus: false,
    classes: { paper: customSelectStyles.menuPaper },
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' }
  };

  const maxExpiry = useMemo(() => endOfDayDate(selectedTime), [selectedTime]);
  const minutesToEod = useMemo(() => diffMinutes(selectedTime, maxExpiry), [selectedTime, maxExpiry]);

  const hourOptions: number[] = useMemo(() => {
    const hours = Math.floor(minutesToEod / 60);
    return Array.from({ length: Math.max(0, hours) }, (_, i) => i + 1);
  }, [minutesToEod]);

  const [selectValue, setSelectValue] = useState<SelectValue>(() => {
    if (expiredTime) {
      const mins = diffMinutes(selectedTime, expiredTime);
      const minsToEod = diffMinutes(selectedTime, endOfDayDate(selectedTime));
      if (mins === minsToEod) return 'eod';
      return Math.max(1, Math.floor(mins / 60));
    }
    return 'eod';
  });

  // Clamp hours when the remaining window shrinks
  useEffect(() => {
    if (selectValue === 'eod') return;
    const maxHours = Math.floor(minutesToEod / 60);

    if (maxHours <= 0) {
      setSelectValue('eod');
      return;
    }

    if (selectValue > maxHours) setSelectValue(maxHours);
  }, [minutesToEod, selectValue]);

  // Emit effective expiry when selectedTime or selection changes
  useEffect(() => {
    const next = selectValue === 'eod' ? maxExpiry : addMinutesDate(selectedTime, selectValue * 60);

    const currentMs = expiredTime ? new Date(expiredTime).getTime() : null;
    const nextMs = next.getTime();

    if (currentMs !== nextMs) {
      onExpiredTimeSelected(next);
    }
  }, [selectedTime, selectValue, maxExpiry, expiredTime, onExpiredTimeSelected]);

  return (
    <div className={styles.container}>
      <Text variant="nlg">Keep trying for</Text>
      <FormControl fullWidth>
        <Select
          disabled={disabled}
          MenuProps={menuProps}
          value={selectValue}
          className={customSelectStyles.select}
          error={!!error}
          onChange={(event) => {
            const v = event.target.value as SelectValue;
            setSelectValue(v);
            const next = v === 'eod' ? maxExpiry : addMinutesDate(selectedTime, (v as number) * 60);
            onExpiredTimeSelected(next);
          }}
          sx={{ ...(error && customErrorBorderStyles) }}
          renderValue={(val) => {
            if (val === 'eod') {
              return <Text variant="md">End of day ({formatTime12(maxExpiry)})</Text>;
            }
            const expiry = addMinutesDate(selectedTime, val * 60);
            return (
              <Text variant="md">
                {fullHoursToText(val * 60)} ({formatTime12(expiry)})
              </Text>
            );
          }}
        >
          <MenuItem value="eod" key="eod">
            <Text variant="md">End of day ({formatTime12(maxExpiry)})</Text>
          </MenuItem>

          {hourOptions.map((hours) => {
            const expiry = addMinutesDate(selectedTime, hours * 60);
            return (
              <MenuItem value={hours} key={hours}>
                <Text variant="md">
                  {fullHoursToText(hours * 60)} ({formatTime12(expiry)})
                </Text>
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {!disabled && error && (
        <Text variant="sm" className={styles.error}>
          {error}
        </Text>
      )}
    </div>
  );
};
